import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { SkeletonBlock, SkeletonLine } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { subscribeToUser } from '../../lib/realtime';
import { copy } from '../../config/copy';
import { useSession } from '../../stores/session';

interface Actor {
  username: string;
  display_name: string | null;
  avatar: string | null;
}

interface Subject {
  type: 'track';
  id: number;
  title: string;
  artwork_small: string | null;
  status: string;
}

interface Item {
  /** Every row this entry collapsed, so marking it read marks all of them. */
  ids: number[];
  type:
    | 'follow'
    | 'like'
    | 'credit_added'
    | 'track_ready'
    | 'track_failed'
    | 'host_approved'
    | 'host_declined'
    | 'host_revoked'
    | 'station_review'
    | 'boost_earned'
    | 'achievement_earned'
    | 'dmca_takedown'
    | 'dmca_restored';
  unread: boolean;
  created_at: string;
  actors: Actor[];
  actor_count: number;
  subject: Subject | null;
  /*
   * Whatever the event carried. `reason` comes from a failed transcode,
   * `note` from a host application decision, `rating` from a host review - all
   * optional, because most notification types carry none of them.
   */
  data: { reason?: string; note?: string; rating?: number; label?: string } | null;
}

interface Inbox {
  items: Item[];
  unread: number;
}

/**
 * How often the bell asks when it is not being told.
 *
 * The socket now pushes `notification.created` (§8.1), so this is the backstop
 * rather than the mechanism: it covers a dropped connection, a sleeping laptop,
 * and the case where realtime is not configured at all. Five minutes rather
 * than one, because the common path no longer waits for it.
 */
const POLL_MS = 300_000;

/**
 * The notification bell.
 *
 * Pushed over Soketi, with a poll behind it. The socket says *something
 * changed* and this refetches — §8.2's rule, and what makes a dropped event
 * cost latency rather than correctness. If realtime is not configured, the poll
 * alone still works and nothing else changes.
 */
export function NotificationBell() {
  const user = useSession((state) => state.user);
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery<Inbox>({
    queryKey: ['notifications'],
    queryFn: () => api.get<Inbox>('/me/notifications'),
    refetchInterval: POLL_MS,
    // A tab nobody is looking at does not need to keep asking.
    refetchIntervalInBackground: false,
  });

  /*
   * The socket says *something changed*; this asks what.
   *
   * §8.2's rule made concrete - the event carries a type and nothing else, and
   * the inbox is rebuilt from the API. That is what makes a dropped event cost
   * latency rather than correctness.
   */
  useEffect(() => {
    if (!user) return;

    return subscribeToUser(user.id, () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });

      /*
       * And the boost balance, which earning one also changes.
       *
       * Unconditional because the event carries only its name - `boost_earned`
       * and a new follower arrive identically here - and the alternative is
       * putting the payload on the wire, which §8.2 is explicit about not
       * doing. One small GET on an event that fires rarely per person is a
       * better trade than a count that stays wrong until the page reloads.
       */
      void queryClient.invalidateQueries({ queryKey: ['my-boosts'] });
    });
  }, [user, queryClient]);

  const markRead = useMutation({
    mutationFn: (ids?: number[]) => api.post('/me/notifications/read', ids ? { ids } : {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Close on a click outside or on Escape. Both, because a dropdown that only
  // closes one way is a dropdown somebody gets stuck in.
  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = data?.unread ?? 0;

  return (
    <div className="relative" ref={panel}>
      <button
        type="button"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        data-notification-bell
        data-unread={unread}
        onClick={() => setOpen((current) => !current)}
        className="relative flex h-9 w-9 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink"
      >
        <svg viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M10 3a4.5 4.5 0 0 0-4.5 4.5c0 3-1 4.5-1.5 5h12c-.5-.5-1.5-2-1.5-5A4.5 4.5 0 0 0 10 3Z" />
          <path d="M8.2 15a1.9 1.9 0 0 0 3.6 0" />
        </svg>

        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-[var(--radius-pill)] bg-hot px-1 text-[10px] font-semibold text-white"
            // Announced by the button's label instead; two readings of the same
            // number is noise.
            aria-hidden="true"
          >
            {/* Capped, because a three-digit badge stops being a number and
                starts being a shape. */}
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-40 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-xl"
          data-notification-panel
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h2 className="text-sm font-semibold text-ink">Notifications</h2>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate(undefined)}
                className="text-xs text-accent hover:underline"
                data-mark-all-read
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[24rem] overflow-y-auto">
            {isPending ? (
              <div className="flex flex-col gap-4 px-4 py-4" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <SkeletonBlock className="h-8 w-8 shrink-0 rounded-[var(--radius-pill)]" />
                    <SkeletonLine width={i === 1 ? '60%' : '80%'} />
                  </div>
                ))}
              </div>
            ) : (data?.items.length ?? 0) === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-soft">Nothing yet.</p>
            ) : (
              <ul>
                {data!.items.map((item) => (
                  <li key={item.ids.join('-')} data-notification={item.type}>
                    <Row
                      item={item}
                      onOpen={() => {
                        if (item.unread) markRead.mutate(item.ids);
                        setOpen(false);
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ item, onOpen }: { item: Item; onOpen: () => void }) {
  const target = href(item);
  const lead = item.actors[0];

  return (
    <Link
      to={target}
      onClick={onOpen}
      className={`flex gap-3 border-b border-line px-4 py-3 transition-colors last:border-0 hover:bg-raised ${
        item.unread ? 'bg-raised/40' : ''
      }`}
      data-unread={item.unread ? 'true' : 'false'}
    >
      {lead ? (
        <Avatar src={lead.avatar} name={lead.display_name || lead.username} size={32} />
      ) : (
        // No actor means the system did it - a transcode finishing. A generic
        // mark rather than a fake person.
        <span
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-slate text-xs text-soft"
        >
          ♪
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug text-soft">{sentence(item)}</p>
        <p className="meta-caps mt-1">{ago(item.created_at)}</p>
      </div>

      {item.unread && (
        <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />
      )}
    </Link>
  );
}

/**
 * Where the entry goes when clicked.
 *
 * A notification you cannot act on is an announcement. Each of these lands
 * somewhere the next step is obvious — a credit request on Settings, where the
 * accept button is.
 */
function href(item: Item): string {
  if (item.type === 'credit_added') return '/settings';
  // Both hosting decisions land on the same page, which reads the current state
  // and shows the panel, the decline note, or the form accordingly.
  if (item.type.startsWith('host_')) return '/host';
  if (item.subject?.type === 'track') return `/tracks/${item.subject.id}`;
  if (item.type === 'follow' && item.actors[0]) return `/${item.actors[0].username}`;

  return '/';
}

/** The collapsed sentence: "Alice and 4 others favourited Moonlight". */
function sentence(item: Item): React.ReactNode {
  const who = names(item);
  const title = item.subject?.title ?? 'a track';

  switch (item.type) {
    case 'follow':
      return <>{who} followed you.</>;
    case 'like':
      return (
        <>
          {who} favourited <span className="font-medium text-ink">{title}</span>.
        </>
      );
    case 'credit_added':
      return (
        <>
          {who} credited you on <span className="font-medium text-ink">{title}</span>. It will not
          appear until you accept it.
        </>
      );
    case 'track_ready':
      return (
        <>
          <span className="font-medium text-ink">{title}</span> is ready to play.
        </>
      );
    case 'track_failed':
      return (
        <>
          <span className="font-medium text-ink">{title}</span> could not be processed.
          {item.data?.reason ? ` ${item.data.reason}` : ''}
        </>
      );
    case 'host_approved':
      return (
        <>
          <span className="font-medium text-ink">You can host now.</span> Your application was
          accepted.
          {item.data?.note ? ` ${item.data.note}` : ''}
        </>
      );
    /*
     * Phrased as "not this time", not "rejected".
     *
     * A decline here is usually a thin pitch rather than a judgement on the
     * person, and they can apply again the moment they close this. Wording that
     * sounds final would stop the ones worth having from trying twice.
     */
    case 'host_declined':
      return (
        <>
          Your host application was not accepted this time.
          {item.data?.note ? ` ${item.data.note}` : ''}
        </>
      );
    /*
     * Says what changed and not why. The reason is a moderation decision, and
     * the notification is not the place to argue it - the account's own page
     * and a support conversation are.
     */
    case 'host_revoked':
      return <>You can no longer host rooms.</>;
    /*
     * The rating rides in `data` so the line can be specific without a second
     * request; the words themselves live on the track page. "Played" is the
     * part that matters - it is what separates this from a comment.
     */
    /*
     * Actorless, so it does not start with a name like the rest of this list.
     * Nobody gave it to them.
     */
    /*
     * Both actorless. Which moderator pressed the button is not the point, and
     * naming them invites the wrong conversation.
     */
    case 'dmca_takedown':
      return (
        <>
          <span className="font-medium text-ink">{title}</span> was taken down after a copyright
          claim. You can answer it on your{' '}
          <Link to="/claims" className="text-accent hover:underline">
            claims page
          </Link>
          .
        </>
      );
    case 'dmca_restored':
      return (
        <>
          <span className="font-medium text-ink">{title}</span> is back up — the claim against it
          was withdrawn.
        </>
      );
    case 'achievement_earned':
      return (
        <>
          {/*
            The badge names itself. The notification carries its label so the
            bell does not have to know the catalogue, and does not need a second
            request to say which one somebody earned.
          */}
          <span className="font-medium text-ink">
            You earned {item.data?.label ?? 'a badge'}.
          </span>{' '}
          It is on your profile.
        </>
      );
    case 'boost_earned':
      return (
        <>
          <span className="font-medium text-ink">You earned a {copy['noun.boost.earned']}.</span> Spend it on a
          submission to jump a host's queue.
        </>
      );
    case 'station_review':
      return (
        <>
          {who} played <span className="font-medium text-ink">{title}</span> on air and left you a
          review{item.data?.rating ? ` — ${item.data.rating}/5` : ''}.
        </>
      );
    default:
      return <>Something happened.</>;
  }
}

function names(item: Item): React.ReactNode {
  const [first] = item.actors;

  if (!first) return <span className="font-medium text-ink">Someone</span>;

  const name = <span className="font-medium text-ink">{first.display_name || first.username}</span>;

  if (item.actor_count <= 1) return name;

  // "and 1 other" rather than "and 1 others". Small, and the kind of thing
  // that makes an interface feel unfinished when it is wrong.
  const rest = item.actor_count - 1;

  return (
    <>
      {name} and {rest} {rest === 1 ? 'other' : 'others'}
    </>
  );
}

function ago(iso: string): string {
  const then = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;

  return `${Math.round(minutes / 1440)}d ago`;
}
